import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useContacts, type Contact } from "@/hooks/useContacts";

export const VENDOR_TAG = "equipment-vendor";

/**
 * Every organization contact, tagged as a vendor or not. Vendor pickers show
 * only VENDOR_TAG contacts until the user types, then search all of them (see
 * matchVendors). Name lookups for an already-linked vendor must use this full
 * list too, since a part or PO can point at an untagged contact.
 */
export function useVendorContacts() {
  return useContacts(undefined, undefined, "organization");
}

export function isTaggedVendor(contact: Contact): boolean {
  return (contact.tags ?? []).includes(VENDOR_TAG);
}

/** Tagged vendors when the search is empty; any matching organization once the user types. */
export function matchVendors(contacts: Contact[], search: string): Contact[] {
  const term = search.trim().toLowerCase();
  if (!term) return contacts.filter(isTaggedVendor);
  return contacts.filter((c) => (c.company ?? "").toLowerCase().includes(term));
}

/**
 * Adds VENDOR_TAG to a contact picked as a vendor, so it shows in the default
 * dropdown from then on. Best effort and silent: contact updates are limited
 * to admins and cellar managers by RLS, and for anyone else the vendor link
 * itself still saves -- the contact just stays untagged.
 */
export function useTagAsVendor() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (contact: Contact) => {
      if (isTaggedVendor(contact)) return;
      const { error } = await supabase
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        .from("contact" as any)
        .update({ tags: [...(contact.tags ?? []), VENDOR_TAG] })
        .eq("id", contact.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["contacts"] });
    },
    onError: (error: Error) => {
      console.warn("[vendor] Could not auto-tag contact as vendor:", error.message);
    },
  });
}
